import { ChangeEvent, useMemo, useState } from 'react'
import { NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { HtmlPageMetaResourceService } from '~/components/HtmlPageMeta/HtmlPageMetaResourceService'
import { HtmlPageMeta } from '~/components/HtmlPageMeta/HtmlPageMeta'
import { FeaturedGalleryCard } from '~/components/FeaturedGalleries/FeaturedGalleryCard'
import { GalleriesPageViewModel } from '~/modules/PresentationAdapters/GalleriesPageAdapter'
import styles from './MarketGalleriesPage.module.scss'

export interface MarketGalleriesPageProps {
  viewModel: GalleriesPageViewModel
}

interface ListProps {
  cards: GalleriesPageViewModel['cards']
  title: string
  subtitle: string
  filterLabel: string
}

const MarketGalleriesList = ({ cards, title, subtitle, filterLabel }: ListProps) => {
  const [filter, setFilter] = useState<string>('')

  const filteredCards = useMemo(() => {
    if (filter.trim() === '') {
      return cards
    }

    const normalizedFilter = filter.trim().toLowerCase()

    return cards.filter((card) => {
      return card.title.toLowerCase().includes(normalizedFilter)
    })
  }, [cards, filter])

  const onFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value)
  }

  return (
    <section className={ styles.marketGalleriesPage__container }>
      <header className={ styles.marketGalleriesPage__header }>
        <p className={ styles.marketGalleriesPage__eyebrow }>
          Galleries
        </p>
        <h1 className={ styles.marketGalleriesPage__title }>
          { title }
        </h1>
        <p className={ styles.marketGalleriesPage__subtitle }>
          { subtitle }
        </p>
      </header>

      <div className={ styles.marketGalleriesPage__toolbar }>
        <input
          type="text"
          className={ styles.marketGalleriesPage__filter }
          placeholder={ filterLabel }
          value={ filter }
          onChange={ onFilterChange }
        />

        <p className={ styles.marketGalleriesPage__count }>
          { filteredCards.length } { filteredCards.length === 1 ? 'GALLERY' : 'GALLERIES' }
        </p>
      </div>

      <div className={ styles.marketGalleriesPage__list }>
        { filteredCards.map((card) => {
          return (
            <FeaturedGalleryCard
              key={ card.id }
              title={ card.title }
              gallery={ card.galleryUrl }
              image={ card.imageUrl }
              organizerAssetUrl={ card.organizerAssetUrl }
              organizerName={ card.organizerName }
              appearance="museumHome"
              layout="galleryListing"
            />
          )
        }) }
      </div>
    </section>
  )
}

export const MarketGalleriesPage: NextPage<MarketGalleriesPageProps> = ({ viewModel }) => {
  const { t } = useTranslation('galleries')
  const htmlPageMetaUrlProps = (
    new HtmlPageMetaResourceService(t('galleries_page_title'), t('galleries_page_description'))
  ).getProperties()
  const htmlPageMetaProps = { ...viewModel.htmlPageMetaContextProps, ...htmlPageMetaUrlProps }

  return (
    <>
      <HtmlPageMeta { ...htmlPageMetaProps } />

      <div className={ styles.marketGalleriesPage }>
        <MarketGalleriesList
          cards={ viewModel.cards }
          title={ t('galleries_title') }
          subtitle={ t('galleries_page_description') }
          filterLabel={ t('galleries_filter') }
        />
      </div>
    </>
  )
}
